import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  getInvoicesMissingXeroId,
  runBackfillXeroInvoiceIds,
} from '../services/xeroIntegrationService';
import type {
  InvoiceMissingXeroIdRow,
  Pagination,
} from '../services/xeroIntegrationService';

interface XeroInvoicesMissingPageProps {
  onBack: () => void;
}

const PAGE_SIZE = 25;

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return iso.length > 10 ? iso.slice(0, 10) : iso;
};

const formatTotal = (n: number | null): string => {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'AUD' });
};

export const XeroInvoicesMissingPage: React.FC<XeroInvoicesMissingPageProps> = ({ onBack }) => {
  const [page, setPage] = useState(1);
  const [includeSkipped, setIncludeSkipped] = useState(false);
  const [items, setItems] = useState<InvoiceMissingXeroIdRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryingGuid, setRetryingGuid] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await getInvoicesMissingXeroId(
        page,
        PAGE_SIZE,
        { include_previously_skipped: includeSkipped || undefined },
        controller.signal
      );
      setItems(data.items);
      setPagination(data.pagination);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page, includeSkipped]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const handleRetry = async (row: InvoiceMissingXeroIdRow) => {
    if (row.cr001_number === null || row.cr001_number === undefined) {
      setSnackbar({ open: true, message: 'Row has no cr001_number; cannot retry.', severity: 'error' });
      return;
    }
    setRetryingGuid(row.guid);
    try {
      await runBackfillXeroInvoiceIds({
        cr001_number: row.cr001_number,
        retry_previously_skipped: true,
      });
      setSnackbar({ open: true, message: `Backfill triggered for cr001_number=${row.cr001_number}`, severity: 'success' });
      load();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Retry failed';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setRetryingGuid(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
        >
          Back
        </Button>
        <Typography variant="h6" sx={{ ml: 1 }}>
          Invoices missing Xero ID
        </Typography>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={
            <Switch
              checked={includeSkipped}
              onChange={(e) => { setIncludeSkipped(e.target.checked); setPage(1); }}
              size="small"
            />
          }
          label="Include previously skipped"
        />
        <IconButton onClick={load} size="small" disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.08)', '& .MuiTableCell-head': { color: 'rgba(0,0,0,0.8)', fontSize: '0.75rem' } }}>
                <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>cr001_number</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Lookup status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={20} /></TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">No invoices missing Xero ID.</TableCell></TableRow>
              )}
              {!loading && items.map((row, index) => (
                <TableRow
                  key={row.guid}
                  sx={{
                    backgroundColor: index % 2 === 1 ? 'action.hover' : 'transparent',
                    '& .MuiTableCell-root': { fontSize: '0.8rem' },
                  }}
                >
                  <TableCell>{row.cr001_number ?? '—'}</TableCell>
                  <TableCell>{formatDate(row.date)}</TableCell>
                  <TableCell align="right">{formatTotal(row.total)}</TableCell>
                  <TableCell>
                    {row.customer.fullname ?? row.customer.business_name ?? '—'}
                    {row.customer.cr001_id && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        cr001_id={row.customer.cr001_id}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.lookup_status
                      ? <Chip label={row.lookup_status} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        retryingGuid === row.guid
                          ? <CircularProgress size={14} />
                          : <ReplayIcon fontSize="small" />
                      }
                      disabled={retryingGuid === row.guid || row.cr001_number === null || row.cr001_number === undefined}
                      onClick={() => handleRetry(row)}
                    >
                      Retry backfill
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {pagination && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, p: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Page {pagination.page} of {pagination.totalPages || 1} · {pagination.totalItems.toLocaleString()} total
            </Typography>
            <IconButton size="small" disabled={!pagination.hasPrevious || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" disabled={!pagination.hasNext || loading} onClick={() => setPage((p) => p + 1)}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
